// MiniMax H3 — one page for text-to-video, start/end-frame and reference
// generation, all with native audio. The three "Start with" modes share the
// prompt and options; only the active mode's attachments are submitted, and
// the queue task (t2va / fl2va / ref2va) follows from what is attached.

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFieldsRounded';
import FramesIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import RefsIcon from '@mui/icons-material/CollectionsOutlined';
import { useTranslation, format } from '../i18n';
import { Section } from '../components/Section';
import { JobResult } from '../components/JobResult';
import { RecentGenerationsStrip } from '../components/RecentGenerationsStrip';
import { getFingerprintNow } from '../hooks/useFingerprint';
import { useSignupNudge } from '../components/SignupNudge';
import { usePriceQuote } from '../hooks/usePriceQuote';
import { AssetInput, uploadAsset } from '../components/generator/AssetInput';
import type { UploadResult } from '../components/generator/FrameInput';
import {
  KeyframesInput,
  keyframeSlotIssues,
  newKeyframeSlot,
  parseKeyframeTime,
  type KeyframeSlot,
} from '../components/generator/KeyframesInput';
import { takeDraft } from '../lib/landingDraft';
import {
  H3_ASPECT_RATIOS,
  H3_DURATION_DEFAULT,
  H3_DURATION_MIN,
  H3_ENDPOINT,
  H3_GENERATE_URL,
  H3_MAX_ANCHORS,
  H3_MAX_REF_AUDIO,
  H3_MAX_REF_IMAGES,
  H3_PROMPT_MAX_CHARS,
  H3_SHORT_EDGES,
  H3_SHORT_EDGE_LABELS,
  H3_T2V_ASPECT_RATIOS,
  H3_UPLOAD_URL,
  maxDurationFor,
  normalizeRemix,
  type H3AspectRatio,
  type H3Mode,
  type H3RemixState,
  type H3ShortEdge,
} from '../lib/h3';

// Padding of the generator card; the sticky Generate footer bleeds out by
// the same amount so it spans the card edge to edge.
const CARD_PX = { xs: 3, md: 4 };

type Slots = (UploadResult | null)[];
const emptySlots = (n: number): Slots => Array.from({ length: n }, () => null);
const present = (slots: Slots): UploadResult[] => slots.filter((s): s is UploadResult => !!s);

export default function MinimaxH3Video() {
  const t = useTranslation();
  const h = t.minimaxH3;
  const location = useLocation();
  // Remix from History: H3 rows come through as-is, legacy LTX/WAN rows are
  // mapped onto H3 settings (always 768p) so old jobs re-run on the new queue.
  const remixState = (location.state as H3RemixState | null) ?? null;
  const remix = normalizeRemix(remixState?.remix ?? null, remixState?.remixFrom);
  const remixActivityId = remixState?.remixActivityId;

  const remixMode: H3Mode = remix?.first_frame_r2_key || remix?.last_frame_r2_key || remix?.keyframes?.length
    ? 'frames'
    : remix?.reference_image_r2_keys?.length || remix?.reference_video_r2_key
      ? 'refs'
      : 'text';
  const [mode, setMode] = useState<H3Mode>(remixMode);
  const [prompt, setPrompt] = useState(remix?.prompt ?? '');
  const [duration, setDuration] = useState(remix?.duration ?? H3_DURATION_DEFAULT);
  const [shortEdge, setShortEdge] = useState<H3ShortEdge>(remix?.short_edge ?? 768);
  const [aspect, setAspect] = useState<H3AspectRatio>(remix?.aspect_ratio ?? (remixMode === 'text' ? '16:9' : 'auto'));
  const [quality, setQuality] = useState(remix?.tier === 'base');

  const [firstFrame, setFirstFrame] = useState<UploadResult | null>(null);
  const [lastFrame, setLastFrame] = useState<UploadResult | null>(null);
  const [keyframes, setKeyframes] = useState<KeyframeSlot[]>([]);
  const [refImages, setRefImages] = useState<Slots>(emptySlots(H3_MAX_REF_IMAGES));
  const [refVideo, setRefVideo] = useState<UploadResult | null>(null);
  const [refAudio, setRefAudio] = useState<Slots>(emptySlots(H3_MAX_REF_AUDIO));

  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ activity_id: number; request_id: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hasRef = present(refImages).length > 0 || !!refVideo || present(refAudio).length > 0;
  const tier = quality ? 'base' : 'turbo';

  // Text-only has no input to match, so `auto` is only offered with media.
  const aspectOptions = mode === 'text' ? H3_T2V_ASPECT_RATIOS : H3_ASPECT_RATIOS;
  const changeMode = (next: H3Mode) => {
    setMode(next);
    if (next === 'text' && aspect === 'auto') setAspect('16:9');
    if (next !== 'text' && mode === 'text') setAspect('auto');
  };

  // The engine caps clip length by canvas (megapixel·seconds); keep the
  // slider inside that envelope whenever resolution or aspect changes.
  const durationCap = useMemo(() => maxDurationFor(shortEdge, aspect), [shortEdge, aspect]);
  useEffect(() => {
    if (duration > durationCap) setDuration(durationCap);
  }, [duration, durationCap]);

  // Quote both tiers so the Quality checkbox can show its real surcharge.
  const turboCredits = usePriceQuote({ endpoint: H3_ENDPOINT, model: 'turbo', minHeight: shortEdge, duration });
  const baseCredits = usePriceQuote({ endpoint: H3_ENDPOINT, model: 'base', minHeight: shortEdge, duration });
  const credits = quality ? baseCredits : turboCredits;
  const surchargePerSec =
    turboCredits != null && baseCredits != null && duration > 0
      ? Math.max(0, Math.round((baseCredits - turboCredits) / duration))
      : 2;

  // Interior keyframes: 8 anchors in all, first and last included.
  const maxKeyframes = H3_MAX_ANCHORS - (firstFrame ? 1 : 0) - (lastFrame ? 1 : 0);
  const keyframesInvalid =
    mode === 'frames' && (keyframes.length > maxKeyframes || keyframeSlotIssues(keyframes, duration).some((i) => i !== null));

  const canSubmit = prompt.trim().length > 0 && !submitting && !keyframesInvalid;
  const { gateGenerate } = useSignupNudge();

  // Landing-page draft handoff: prefill the prompt and, if the visitor picked
  // an image, upload it as the start frame.
  useEffect(() => {
    const draft = takeDraft();
    if (!draft) return;
    if (draft.prompt) setPrompt(draft.prompt);
    if (draft.file) {
      changeMode('frames');
      void uploadAsset(H3_UPLOAD_URL, 'image', draft.file)
        .then((r) => setFirstFrame(r))
        .catch((err) => console.warn('landing draft upload failed:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remix handoff: the stored input only has r2 keys (the original presigned
  // URLs expired long ago), so re-presign them through the source activity
  // and drop the media back into the tiles.
  useEffect(() => {
    if (!remix || !remixActivityId) return;
    const keys = [
      remix.first_frame_r2_key,
      remix.last_frame_r2_key,
      ...(remix.keyframes ?? []).map((k) => k.r2_key),
      ...(remix.reference_image_r2_keys ?? []),
      remix.reference_video_r2_key,
      ...(remix.reference_audio_r2_keys ?? []),
    ].filter((k): k is string => !!k);
    if (keys.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/v1/history/${remixActivityId}/presigned-url`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ r2_keys: keys }),
        });
        if (!res.ok) throw new Error(`presign failed: ${res.status}`);
        const { urls } = (await res.json()) as { urls: string[] };
        if (cancelled) return;
        const byKey = new Map(keys.map((k, i) => [k, urls[i]]));
        const asset = (k?: string): UploadResult | null => (k && byKey.get(k) ? { url: byKey.get(k)!, r2_key: k } : null);
        const fill = (n: number, ks: string[] = []): Slots => Array.from({ length: n }, (_, i) => asset(ks[i]));
        setFirstFrame(asset(remix.first_frame_r2_key));
        setLastFrame(asset(remix.last_frame_r2_key));
        setKeyframes((remix.keyframes ?? []).map((k) => newKeyframeSlot(k.time, asset(k.r2_key))));
        setRefImages(fill(H3_MAX_REF_IMAGES, remix.reference_image_r2_keys));
        setRefVideo(asset(remix.reference_video_r2_key));
        setRefAudio(fill(H3_MAX_REF_AUDIO, remix.reference_audio_r2_keys));
      } catch (err) {
        console.warn('remix media presign failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remixActivityId]);

  const handleSubmit = async () => {
    if (gateGenerate()) return;
    setSubmitting(true);
    setError(null);
    try {
      const fingerprint = await getFingerprintNow();
      const body: Record<string, unknown> = {
        prompt,
        duration,
        short_edge: shortEdge,
        aspect_ratio: aspect,
        tier,
        fingerprint,
      };
      if (mode === 'frames') {
        body.first_frame_url = firstFrame?.url;
        body.first_frame_r2_key = firstFrame?.r2_key;
        body.last_frame_url = lastFrame?.url;
        body.last_frame_r2_key = lastFrame?.r2_key;
        if (keyframes.length) {
          // Sorted by time, as the worker expects them; validated above.
          body.keyframes = keyframes
            .filter((k) => k.image)
            .map((k) => ({ url: k.image!.url, r2_key: k.image!.r2_key, time: parseKeyframeTime(k.time)! }))
            .sort((a, b) => a.time - b.time);
        }
      } else if (mode === 'refs') {
        const imgs = present(refImages);
        const auds = present(refAudio);
        if (imgs.length) {
          body.reference_image_urls = imgs.map((i) => i.url);
          body.reference_image_r2_keys = imgs.map((i) => i.r2_key);
        }
        if (refVideo) {
          body.reference_video_url = refVideo.url;
          body.reference_video_r2_key = refVideo.r2_key;
        }
        if (auds.length) {
          body.reference_audio_urls = auds.map((a) => a.url);
          body.reference_audio_r2_keys = auds.map((a) => a.r2_key);
        }
      }
      const res = await fetch(H3_GENERATE_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 402) {
        const b = await res.json();
        throw new Error(`Insufficient credits (need ${b.required}, have ${b.available})`);
      }
      if (!res.ok) {
        const b = await res.text().catch(() => '');
        throw new Error(`submit failed: ${res.status} ${b}`);
      }
      const data = await res.json();
      setSubmitResult({ activity_id: data.activity_id, request_id: data.request_id });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNew = () => {
    setSubmitResult(null);
    setPrompt('');
    setFirstFrame(null);
    setLastFrame(null);
    setKeyframes([]);
    setRefImages(emptySlots(H3_MAX_REF_IMAGES));
    setRefVideo(null);
    setRefAudio(emptySlots(H3_MAX_REF_AUDIO));
  };

  const clearRefs = () => {
    setRefImages(emptySlots(H3_MAX_REF_IMAGES));
    setRefVideo(null);
    setRefAudio(emptySlots(H3_MAX_REF_AUDIO));
  };

  const durationMarks = useMemo(() => {
    const pts = [5, 10, 15, 20, 25, 30].filter((v) => v <= durationCap);
    return pts.map((v) => ({ value: v, label: `${v}s` }));
  }, [durationCap]);

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {submitResult ? (
          <Section title={h.resultTitle}>
            <Paper sx={{ p: 4 }}>
              <JobResult
                activityId={submitResult.activity_id}
                resultType="video"
                onReset={handleNew}
                resetLabel={h.newGeneration}
              />
            </Paper>
          </Section>
        ) : (
          <Paper sx={{ px: CARD_PX, pt: CARD_PX, pb: 0, overflow: 'visible' }}>
            <Stack spacing={2.5}>
              <Typography variant="h5" sx={{ fontSize: 24, lineHeight: 1.25 }}>
                {h.title}
              </Typography>

              {/* Start with: text / frames / references */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {h.startWith}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={mode}
                  onChange={(_, v: H3Mode | null) => v && changeMode(v)}
                  aria-label={h.startWith}
                  size="small"
                  sx={{
                    p: 0.5,
                    gap: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2.5,
                    bgcolor: 'background.default',
                    '& .MuiToggleButtonGroup-grouped': {
                      border: '1px solid transparent !important',
                      borderRadius: '8px !important',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: 13,
                      color: 'text.primary',
                      gap: 0.75,
                      py: 0.75,
                      '&.Mui-selected': {
                        bgcolor: 'rgba(124, 111, 240, 0.14)',
                        borderColor: 'primary.main !important',
                        color: '#d7d1ff',
                      },
                    },
                  }}
                >
                  <ToggleButton value="text">
                    <TextFieldsIcon fontSize="small" /> {h.modeText}
                  </ToggleButton>
                  <ToggleButton value="frames">
                    <FramesIcon fontSize="small" /> {h.modeFrames}
                  </ToggleButton>
                  <ToggleButton value="refs">
                    <RefsIcon fontSize="small" /> {h.modeRefs}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {mode === 'frames' && (
                <Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                    }}
                  >
                    <AssetInput
                      uploadUrl={H3_UPLOAD_URL}
                      kind="image"
                      label={h.startFrame}
                      badge="required"
                      badgeLabel={h.required}
                      value={firstFrame}
                      onChange={setFirstFrame}
                      onClear={() => setFirstFrame(null)}
                      onError={setError}
                    />
                    <AssetInput
                      uploadUrl={H3_UPLOAD_URL}
                      kind="image"
                      label={h.endFrame}
                      badge="optional"
                      badgeLabel={h.optional}
                      value={lastFrame}
                      onChange={setLastFrame}
                      onClear={() => setLastFrame(null)}
                      onError={setError}
                    />
                  </Box>
                  <KeyframesInput
                    uploadUrl={H3_UPLOAD_URL}
                    value={keyframes}
                    onChange={setKeyframes}
                    duration={duration}
                    max={maxKeyframes}
                    onError={setError}
                  />
                </Box>
              )}

              {mode === 'refs' && (
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <Stack spacing={2.5}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">{h.refImages}</Typography>
                        <Button size="small" variant="text" onClick={clearRefs} disabled={!hasRef} sx={{ color: 'text.secondary' }}>
                          {h.clear}
                        </Button>
                      </Stack>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                        {refImages.map((slot, i) => (
                          <AssetInput
                            key={i}
                            uploadUrl={H3_UPLOAD_URL}
                            kind="image"
                            label={`Image ${i + 1}`}
                            value={slot}
                            onChange={(v) => setRefImages((s) => s.map((x, j) => (j === i ? v : x)))}
                            onClear={() => setRefImages((s) => s.map((x, j) => (j === i ? null : x)))}
                            aspectRatio="1 / 1"
                            onError={setError}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {h.refVideo}
                      </Typography>
                      <AssetInput
                        uploadUrl={H3_UPLOAD_URL}
                        kind="video"
                        label="Video 1"
                        value={refVideo}
                        onChange={setRefVideo}
                        onClear={() => setRefVideo(null)}
                        aspectRatio="16 / 5"
                        onError={setError}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {h.refAudio}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                        {refAudio.map((slot, i) => (
                          <AssetInput
                            key={i}
                            uploadUrl={H3_UPLOAD_URL}
                            kind="audio"
                            label={`Audio ${i + 1}`}
                            value={slot}
                            onChange={(v) => setRefAudio((s) => s.map((x, j) => (j === i ? v : x)))}
                            onClear={() => setRefAudio((s) => s.map((x, j) => (j === i ? null : x)))}
                            aspectRatio="1 / 1"
                            onError={setError}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Prompt */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {h.prompt}{' '}
                  <Box component="span" sx={{ color: '#e879a8', fontWeight: 500 }}>
                    {h.promptRequired}
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder={h.promptPlaceholder}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value.slice(0, H3_PROMPT_MAX_CHARS))}
                  inputProps={{ maxLength: H3_PROMPT_MAX_CHARS, 'aria-label': h.prompt }}
                />
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: 'text.secondary' }}>
                  {prompt.length}/{H3_PROMPT_MAX_CHARS}
                </Typography>
              </Box>

              {/* Options: aspect · resolution · duration · quality */}
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {h.settings}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                  <TextField
                    select
                    label={h.aspectRatio}
                    value={aspect}
                    onChange={(e) => setAspect(e.target.value as H3AspectRatio)}
                    size="small"
                  >
                    {aspectOptions.map((a) => (
                      <MenuItem key={a} value={a}>
                        {a === 'auto' ? h.aspectAuto : a}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={h.resolution}
                    value={shortEdge}
                    onChange={(e) => setShortEdge(Number(e.target.value) as H3ShortEdge)}
                    size="small"
                  >
                    {H3_SHORT_EDGES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {H3_SHORT_EDGE_LABELS[s]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ px: 0.5 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2">{h.duration}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                      {duration}s
                    </Typography>
                  </Stack>
                  <Slider
                    value={duration}
                    onChange={(_, v) => setDuration(v as number)}
                    min={H3_DURATION_MIN}
                    max={durationCap}
                    step={1}
                    marks={durationMarks}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}s`}
                    aria-label={h.duration}
                  />
                </Box>
                <Box sx={{ mt: 0.5 }}>
                  <FormControlLabel
                    control={<Checkbox checked={quality} onChange={(e) => setQuality(e.target.checked)} size="small" />}
                    label={
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        {h.qualityMode}{' '}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {format(h.qualitySurcharge, { credits: surchargePerSec })}
                        </Box>
                      </Typography>
                    }
                  />
                </Box>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}

              {/* Generate lives in a footer that sticks to the bottom of the
                  viewport while the card is scrolled, so it is always reachable. */}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2,
                  mx: { xs: -3, md: -4 },
                  px: CARD_PX,
                  py: 2,
                  bgcolor: 'background.paper',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  borderBottomLeftRadius: 16,
                  borderBottomRightRadius: 16,
                }}
              >
                <Button variant="contained" size="large" fullWidth disabled={!canSubmit} onClick={handleSubmit}>
                  {submitting ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    `${h.generate}${credits != null ? ` (${credits} ${t.credits.balanceUnit})` : ''}`
                  )}
                </Button>
              </Box>
            </Stack>
          </Paper>
        )}
      </Box>
      <RecentGenerationsStrip endpoint={H3_ENDPOINT} />
    </Box>
  );
}
